import { useEffect, useRef } from 'react';
import CloseIcon from '../Icons/CloseIcon';
import ArrowBack from '../Icons/ArrowBack';

export interface ModalMobileProps {
  visible?: boolean;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  height?: string;
  bodyClassName?: string;
  titleClassName?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  hideHeader?: boolean;
  icon?: React.ReactNode;
  id?: string;
  btnCloseTestId?: string;
  scrollBehaviour?: boolean;
  showOnLargeScreen?: boolean;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  modalClassName?: string;
  headerClassName?: string;
  isProgressBar?: boolean;
  progressBarProgress?: number;
  isCloseClickOutside?: boolean;
  isRequestNotificationModal?: boolean;
}

const ModalMobile = ({
  children,
  icon,
  btnCloseTestId,
  scrollBehaviour,
  showOnLargeScreen = false,
  showBackButton = false,
  showCloseButton = true,
  visible = false,
  title = '',
  subTitle = '',
  id = 'formContainer',
  height = '50vh',
  bodyClassName = '',
  onClose = () => {},
  hideHeader = false,
  modalClassName = '',
  headerClassName = '',
  isProgressBar = false,
  progressBarProgress = 0,
  titleClassName,
  isCloseClickOutside = true,
  isRequestNotificationModal = false,
}: ModalMobileProps) => {
  const refModal = useRef(null);
  const backdropModalMobileRef = useRef(null);

  useEffect(() => {
    let initialClientY = 0;

    const preventDefault = (e) => {
      e.preventDefault();
    };

    const handleTouchStart = (e) => {
      initialClientY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const modalBounds = refModal.current?.getBoundingClientRect();
      const clientY = e.touches[0].clientY - initialClientY;

      // Check if we are within the modal and allow scrolling inside the modal
      if (
        refModal.current &&
        modalBounds &&
        e.touches[0].clientX >= modalBounds.left &&
        e.touches[0].clientX <= modalBounds.right &&
        e.touches[0].clientY >= modalBounds.top &&
        e.touches[0].clientY <= modalBounds.bottom
      ) {
        // Allow vertical scrolling within modal content
        if (refModal.current.scrollHeight > refModal.current.clientHeight) {
          const isAtTop = refModal.current.scrollTop === 0 && clientY > 0;
          const isAtBottom =
            refModal.current.scrollTop + refModal.current.clientHeight ===
              refModal.current.scrollHeight && clientY < 0;

          // Prevent background scroll when at top or bottom of modal
          if (!isAtTop && !isAtBottom) {
            return;
          }
        }
      }

      // If outside modal bounds or at edges, prevent scrolling
      preventDefault(e);
    };

    const closeOpenMenus = (e) => {
      // Close modal if it's clicked outside the modal but still inside its own backdrop
      if (
        visible &&
        !refModal.current?.contains(e.target) &&
        e.target === backdropModalMobileRef.current &&
        isCloseClickOutside
      ) {
        onClose();
      }
    };

    if (visible) {
      // Lock background scroll
      document.body.style.overflow = 'hidden';

      // Add event listeners
      document.addEventListener('mousedown', closeOpenMenus);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
    }

    return () => {
      // Clean up
      document.body.style.overflow = 'visible';
      document.removeEventListener('mousedown', closeOpenMenus);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [visible]);

  return (
    <div
      ref={backdropModalMobileRef}
      className={`fixed top-0 left-0 w-screen h-screen z-[100] bg-black bg-opacity-30 ${
        visible ? 'block' : 'hidden'
      } ${modalClassName}`}
    >
      {visible && (
        <section
          id={id}
          ref={refModal}
          className={
            `${
              scrollBehaviour ? 'overflow-y-auto' : 'fixed'
            } ${id === 'formContainer' ? 'h-dvh' : ''} flex-col z-[71] ${
              isRequestNotificationModal
                ? 'rounded-xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-w-[328px] w-[90%]'
                : 'bottom-0 left-0 w-full'
            }  items-center justify-center bg-white shadow-xl delay-400 duration-300 ease-in-out transition-all transform ${showOnLargeScreen ? 'xl:hidden' : 'lg:hidden'} ` +
            (isRequestNotificationModal
              ? height
              : visible
                ? `translate-y-0 ${height}`
                : ' translate-y-full h-0')
          }
        >
          {/* Header */}
          {!hideHeader && (
            <header
              className={`flex w-full ${showBackButton ? 'justify-start' : 'justify-between'} gap-4 p-4 ${!isProgressBar && 'mb-2'} border-b border-b-reliableBlack10 ${headerClassName}`}
            >
              {showBackButton && (
                <button
                  className="text-black mb-1"
                  aria-label="close-button"
                  onClick={onClose}
                  data-testid={btnCloseTestId}
                >
                  <ArrowBack size={15} color="#000" />
                </button>
              )}

              <div>
                <h3
                  className={`text-base font-bold text-reliableBlack90 uppercase flex gap-2 items-center ${titleClassName}`}
                >
                  {icon}
                  {title}
                </h3>
                <p className="text-xs font-normal text-reliableBlack90">{subTitle}</p>
              </div>
              {showCloseButton && (
                <button
                  className="text-black"
                  aria-label="close-button"
                  onClick={onClose}
                  data-testid={btnCloseTestId}
                >
                  <CloseIcon size={24} fill="#000" />
                </button>
              )}
            </header>
          )}
          {isProgressBar && (
            <div className="w-full bg-gray-200 h-[3px] dark:bg-gray-700 mb-2">
              <div
                className="bg-[#4cc0ad] h-[3px]"
                style={{ width: `${progressBarProgress}%` }}
              ></div>
            </div>
          )}
          {/* Body */}
          <main className={bodyClassName}>{children}</main>
        </section>
      )}
    </div>
  );
};

export default ModalMobile;
